import { ReactNode, WheelEvent } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { Text } from '@/components/ui/Text'

export interface ShareModalTab {
  id: string
  label: string
}

interface ShareModalShellProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  tabs?: Array<ShareModalTab>
  activeTab?: string
  onTabChange?: (tabId: string) => void
  loading?: boolean
  loadError?: boolean
  onRetry?: () => void
  children: ReactNode
  footer?: ReactNode
  testId?: string
}

export function ShareModalShell(props: ShareModalShellProps) {
  const {
    open,
    onOpenChange,
    title,
    tabs,
    activeTab,
    onTabChange,
    loading,
    loadError,
    onRetry,
    children,
    footer,
    testId,
  } = props

  const { t } = useTranslation('app')

  const hasTabs = !!tabs?.length

  function handleBodyWheel(event: WheelEvent<HTMLDivElement>) {
    const el = event.currentTarget
    if (el.scrollHeight <= el.clientHeight) return

    const nextScrollTop = el.scrollTop + event.deltaY
    const maxScrollTop = el.scrollHeight - el.clientHeight
    const boundedScrollTop = Math.max(0, Math.min(nextScrollTop, maxScrollTop))

    if (boundedScrollTop !== el.scrollTop) {
      event.preventDefault()
      el.scrollTop = boundedScrollTop
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/40 backdrop-blur-[2px]" />
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden p-4 sm:items-center">
          <div
            data-testid={testId}
            className={cn(
              'relative flex min-h-0 w-full max-w-[460px] flex-col overflow-hidden',
              'rounded-2xl border border-border dark:border-transparent bg-background',
              'shadow-xl shadow-black/10 dark:shadow-black/30',
              hasTabs
                ? 'min-h-[360px] max-h-[calc(100dvh-2rem)]'
                : 'max-h-[calc(100dvh-2rem)] sm:max-h-[640px]',
            )}
          >
            {hasTabs ? (
              <div className="flex shrink-0 items-center gap-6 border-b border-border px-6 pt-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    data-testid={`share-tab-${tab.id}`}
                    onClick={() => onTabChange?.(tab.id)}
                    className={cn(
                      'pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
                      activeTab === tab.id
                        ? 'border-foreground text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="share-modal-close"
                  onClick={() => onOpenChange(false)}
                  className="ml-auto pb-2.5 -mb-px text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={t('sharing.closeModalAria')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex shrink-0 items-center justify-between px-6 pt-5 pb-1">
                <h2 className="font-display text-[20px] leading-snug text-foreground">{title}</h2>
                <button
                  type="button"
                  data-testid="share-modal-close"
                  onClick={() => onOpenChange(false)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full',
                    'text-muted-foreground hover:text-foreground hover:bg-accent',
                    'transition-colors',
                  )}
                  aria-label={t('sharing.closeModalAria')}
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            )}

            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
              onWheel={handleBodyWheel}
            >
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : loadError ? (
                <div className="flex flex-col items-center gap-3 py-12 px-6">
                  <Text size="sm" color="muted">{t('sharing.loadFailed')}</Text>
                  <button
                    type="button"
                    data-testid="share-modal-retry"
                    onClick={onRetry}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {t('retry', { ns: 'common' })}
                  </button>
                </div>
              ) : (
                children
              )}
            </div>

            {!loading && !loadError && footer}
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  )
}
